import {createContext, useContext, useEffect, useState} from 'react'
import {safeLocalStorage} from '@/lib/storage'
import {isTenantMode} from '@/lib/runtime-config'

type Theme = 'dark' | 'light' | 'system'

/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */
export const BrandTheme = {
    DEFAULT: 'default',
    LUMON: 'lumon',
    SWISS: 'swiss',
    MUENCHENSTIFT: 'muenchenstift',
    BUNDESDRUCKEREI: 'bundesdruckerei',
    AARGAU: 'aargau',
    TENANT: 'tenant',
} as const
/* eslint-enable i18next/no-literal-string */
export type BrandTheme = typeof BrandTheme[keyof typeof BrandTheme]

type ThemeProviderProps = {
    children: React.ReactNode
    defaultTheme?: Theme
    defaultBrandTheme?: BrandTheme
    storageKey?: string
}

type ThemeProviderState = {
    theme: Theme
    setTheme: (theme: Theme) => void
    resolvedTheme: 'dark' | 'light'
    brandTheme: BrandTheme
    setBrandTheme: (brand: BrandTheme) => void
}

const initialState: ThemeProviderState = {
    theme: 'light',
    setTheme: () => null,
    resolvedTheme: 'light',
    brandTheme: BrandTheme.DEFAULT,
    setBrandTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

const BRAND_THEMES: BrandTheme[] = [BrandTheme.DEFAULT, BrandTheme.LUMON, BrandTheme.SWISS, BrandTheme.MUENCHENSTIFT, BrandTheme.BUNDESDRUCKEREI, BrandTheme.AARGAU, BrandTheme.TENANT]

/* eslint-disable i18next/no-literal-string, no-restricted-syntax -- SVG markup is not translatable; brand favicon colors are fixed brand identity values */
function getBrandFavicon(brand: BrandTheme, mode: 'dark' | 'light'): string | null {
    const iamIcon = (bg: string) =>
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="4" fill="${bg}"/><text x="16" y="21" font-family="system-ui" font-size="10" font-weight="700" fill="white" text-anchor="middle">iam</text></svg>`
    switch (brand) {
        case BrandTheme.LUMON:
            return iamIcon('#1da8d2')
        case BrandTheme.SWISS:
            return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="4" fill="#DC0018"/><rect fill="#fff" x="13" y="6" width="6" height="20"/><rect fill="#fff" x="6" y="13" width="20" height="6"/></svg>'
        case BrandTheme.MUENCHENSTIFT:
            return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="4" fill="#027591"/><path d="M2,28 L2,22 L4,22 L4,20 L6,20 L6,16 L7,12 L8,16 L9,16 L9,14 L10,8 L11,14 L12,16 L13,16 L13,18 L15,18 L15,14 L16,8 L17,4 L18,8 L19,14 L20,16 L21,16 L21,14 L22,10 L23,14 L24,16 L25,16 L25,18 L27,18 L27,16 L28,12 L29,16 L30,20 L30,22 L30,28 Z" fill="white"/></svg>'
        case BrandTheme.BUNDESDRUCKEREI:
            return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="4" fill="#0043CE"/><path d="M16 4L8 8v7c0 6.3 3.4 12.2 8 13.6 4.6-1.4 8-7.3 8-13.6V8l-8-4zm0 2.2l6 3v5.8c0 5-2.6 9.6-6 11-3.4-1.4-6-6-6-11V9.2l6-3z" fill="white" fill-opacity="0.3"/><circle cx="16" cy="14" r="2.5" fill="white"/><path d="M14.8 16h2.4v5h-2.4z" fill="white"/></svg>'
        case BrandTheme.AARGAU:
            return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="4" fill="white"/><path d="M2 5 Q8 0 14 5" fill="none" stroke="#248bcc" stroke-width="2.5" stroke-linecap="round"/><path d="M2 10.5 Q8 5.5 14 10.5" fill="none" stroke="#248bcc" stroke-width="2.5" stroke-linecap="round"/><path d="M2 16 Q8 11 14 16" fill="none" stroke="#248bcc" stroke-width="2.5" stroke-linecap="round"/><path d="M2 21.5 Q8 16.5 14 21.5" fill="none" stroke="#248bcc" stroke-width="2.5" stroke-linecap="round"/><polygon points="20,5 21,7.5 24,7.5 21.5,9.2 22.5,11.5 20,9.8 17.5,11.5 18.5,9.2 16,7.5 19,7.5" fill="#248bcc"/><polygon points="28,5 29,7.5 32,7.5 29.5,9.2 30.5,11.5 28,9.8 25.5,11.5 26.5,9.2 24,7.5 27,7.5" fill="#248bcc"/><polygon points="24,14 25,16.5 28,16.5 25.5,18.2 26.5,20.5 24,18.8 21.5,20.5 22.5,18.2 20,16.5 23,16.5" fill="#248bcc"/><text x="16" y="29" font-family="system-ui" font-size="5.5" font-weight="700" fill="#248bcc" text-anchor="middle" letter-spacing="0.5">AARGAU</text></svg>'
        case BrandTheme.DEFAULT:
            return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="4" fill="${mode === 'dark' ? '#e11d48' : '#f43f5e'}"/><path d="M4 18 L9 18 L12 24 L16 8 L20 20 L23 14 L26 18 L28 18" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`
        case BrandTheme.TENANT:
            return null
    }
}
/* eslint-enable i18next/no-literal-string, no-restricted-syntax */

/* eslint-disable i18next/no-literal-string -- localStorage keys and CSS class names */
const DEFAULT_STORAGE_KEY = 'iam-ui-theme'
const TENANT_FAVICON_LIGHT = '/brand/favicon.svg'
const TENANT_FAVICON_DARK = '/brand/favicon-dark.svg'

/** Resolve and apply theme classes synchronously. Called at module level to prevent FOUC. */
function applyThemeToDOM(theme: Theme, brand: BrandTheme): 'dark' | 'light' {
    const root = window.document.documentElement
    root.classList.remove('light', 'dark', ...BRAND_THEMES)

    const resolved: 'dark' | 'light' = theme === 'system'
        ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
        : theme

    root.classList.add(resolved)
    if (brand !== BrandTheme.DEFAULT) root.classList.add(brand)
    return resolved
}

function probeFavicon(url: string): Promise<boolean> {
    return fetch(url, {method: 'HEAD'})
        .then((r) => r.ok)
        .catch(() => false)
}

const darkFaviconExists: Promise<boolean> = probeFavicon(TENANT_FAVICON_DARK)
const lightFaviconExists: Promise<boolean> = probeFavicon(TENANT_FAVICON_LIGHT)

const tenantMode = isTenantMode()

const initialTheme = (safeLocalStorage.get(DEFAULT_STORAGE_KEY) as Theme) || 'light'
const initialBrand: BrandTheme = tenantMode
    ? BrandTheme.TENANT
    : (safeLocalStorage.get(`${DEFAULT_STORAGE_KEY}-brand`) as BrandTheme) || BrandTheme.DEFAULT
const initialResolved = applyThemeToDOM(initialTheme, initialBrand)
/* eslint-enable i18next/no-literal-string */

export function ThemeProvider({
                                  children,
                                  defaultTheme = 'light',
                                  defaultBrandTheme = BrandTheme.DEFAULT,
                                  storageKey = DEFAULT_STORAGE_KEY,
                                  ...props
                              }: ThemeProviderProps) {
    const [theme, setTheme] = useState<Theme>(
        () => (safeLocalStorage.get(storageKey) as Theme) || defaultTheme
    )
    const [brandTheme, setBrandThemeState] = useState<BrandTheme>(
        () => tenantMode
            ? BrandTheme.TENANT
            : (safeLocalStorage.get(`${storageKey}-brand`) as BrandTheme) || defaultBrandTheme
    )

    const [resolvedTheme, setResolvedTheme] = useState<'dark' | 'light'>(initialResolved)

    // Apply theme classes on change (initial apply already happened at module level)
    useEffect(() => {
        const resolved = applyThemeToDOM(theme, brandTheme)
        setResolvedTheme(resolved)

        const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null
        if (!favicon) return

        const svg = getBrandFavicon(brandTheme, resolved)
        if (svg !== null) {
            // eslint-disable-next-line i18next/no-literal-string -- data URI
            favicon.href = `data:image/svg+xml,${encodeURIComponent(svg)}`
            return
        }

        let cancelled = false
        ;(async () => {
            if (resolved === 'dark' && await darkFaviconExists) {
                if (!cancelled) favicon.href = TENANT_FAVICON_DARK
                return
            }
            if (await lightFaviconExists) {
                if (!cancelled) favicon.href = TENANT_FAVICON_LIGHT
            }
        })()
        return () => {
            cancelled = true
        }
    }, [theme, brandTheme])

    // Listen for system theme changes
    useEffect(() => {
        if (theme !== 'system') return

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
        const handleChange = () => {
            const root = window.document.documentElement
            root.classList.remove('light', 'dark')
            // eslint-disable-next-line i18next/no-literal-string
            const resolved = mediaQuery.matches ? 'dark' : 'light'
            root.classList.add(resolved)
            setResolvedTheme(resolved)
        }

        mediaQuery.addEventListener('change', handleChange)
        return () => mediaQuery.removeEventListener('change', handleChange)
    }, [theme])

    const setBrandTheme = (brand: BrandTheme) => {
        if (tenantMode) return
        safeLocalStorage.set(`${storageKey}-brand`, brand)
        setBrandThemeState(brand)
    }

    const value = {
        theme,
        setTheme: (theme: Theme) => {
            safeLocalStorage.set(storageKey, theme)
            setTheme(theme)
        },
        resolvedTheme,
        brandTheme,
        setBrandTheme,
    }

    return (
        <ThemeProviderContext.Provider {...props} value={value}>
            {children}
        </ThemeProviderContext.Provider>
    )
}

export const useTheme = () => {
    const context = useContext(ThemeProviderContext)

    if (context === undefined)
        throw new Error('useTheme must be used within a ThemeProvider')

    return context
}
